import React, { useState, useRef, useMemo } from 'react'
import {
  Users,
  Compass,
  Sword,
  BookOpen,
  Calendar,
  Clapperboard,
  FileText,
  Plus,
  Trash2,
  Edit3,
  Search,
  Link2,
  Sparkles,
  Bold,
  Italic,
  Check,
  Heading,
  ShieldCheck
} from 'lucide-react'
import PillButton from './PillButton'
import { IconButton } from '@astryxdesign/core/IconButton'
import { TextInput } from '@astryxdesign/core/TextInput'
import { Dialog, DialogHeader } from '@astryxdesign/core/Dialog'
import { Layout, LayoutContent, LayoutFooter, HStack } from '@astryxdesign/core/Layout'
import { cn } from '@renderer/lib/utils'
import { marked } from 'marked'
import { TreeList } from '@astryxdesign/core/TreeList'
import type { TreeListItemData } from '@astryxdesign/core/TreeList'
import { renameWikiPageReferences } from '../utils/wikiLinks'
import posthog from 'posthog-js'
import { createId } from '../lib/id'

interface WikiPanelProps {
  wiki: WikiPage[]
  onSaveWiki: (updatedWiki: WikiPage[]) => void
  storyId: string
  selectedItemId: string | null
  onSelectItem: (id: string | null) => void
  /** Pending suggestions from the manuscript sync, reviewed next to the page they argue with. */
  proposals: WikiProposal[]
  /** Resolves true once the server confirms the write, false (never throws) if it failed. */
  onResolveProposal: (proposalId: string, action: 'accept' | 'reject') => Promise<boolean>
  onSyncNow: () => void
}

const WIKI_HOME_ID = 'home'
const MAX_DESCRIPTION_CHARS = 1024
const MAX_PAGE_TOKENS = 5000

const TYPE_ICONS: Record<string, React.JSX.Element> = {
  character: <Users size={14} />,
  relationship: <Link2 size={14} />,
  location: <Compass size={14} />,
  item: <Sword size={14} />,
  lore: <BookOpen size={14} />,
  event: <Calendar size={14} />,
  scene: <Clapperboard size={14} />,
  rule: <ShieldCheck size={14} />,
  note: <FileText size={14} />
}

const typeIcon = (type: string): React.JSX.Element => TYPE_ICONS[type] || <FileText size={14} />

const typeHeading = (type: string): string => {
  const words = type.split(' ').filter(Boolean)
  if (words.length === 0) return 'Notes'
  const last = words[words.length - 1]
  const plural =
    type === 'lore'
      ? last
      : /(?:s|x|z|ch|sh)$/.test(last)
        ? `${last}es`
        : last.endsWith('y')
          ? `${last.slice(0, -1)}ies`
          : `${last}s`
  return [...words.slice(0, -1), plural]
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const estimateTokens = (text: string): number => Math.ceil(text.length / 4)

export default function WikiPanel({
  wiki,
  onSaveWiki,
  storyId,
  selectedItemId,
  onSelectItem,
  proposals,
  onResolveProposal,
  onSyncNow
}: WikiPanelProps): React.JSX.Element {
  const selectedId = selectedItemId
  const [isEditingBody, setIsEditingBody] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  // Local form states (used for buffer during live text editing before/during saves)
  const [editName, setEditName] = useState('')
  const [editType, setEditType] = useState('character')
  const [editDescription, setEditDescription] = useState('')
  const [editBody, setEditBody] = useState('')
  const [homeMarkdown, setHomeMarkdown] = useState('')

  // Collapsible category folders state
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
    character: true,
    location: true,
    lore: true,
    rule: true
  })

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const lastSelectedIdRef = useRef<string | null>(null)

  const selectItem = (id: string | null): void => {
    onSelectItem(id)
  }

  // Sync edit states when selected item changes
  React.useEffect(() => {
    if (!selectedId || selectedId === WIKI_HOME_ID) {
      lastSelectedIdRef.current = selectedId
      setEditName('')
      setEditType('character')
      setEditDescription('')
      setEditBody('')
      setIsEditingBody(false)
      return
    }
    if (selectedId !== lastSelectedIdRef.current) {
      lastSelectedIdRef.current = selectedId
      const page = wiki.find((candidate) => candidate.id === selectedId)
      if (page) {
        setEditName(page.name)
        setEditType(page.type)
        setEditDescription(page.description)
        setEditBody(page.body)
        setIsEditingBody(false)
      }
    }
  }, [selectedId, wiki])

  // Load synthesized Wiki Home markdown whenever it is selected or the wiki changes
  React.useEffect(() => {
    if (selectedId !== WIKI_HOME_ID) return
    let cancelled = false
    void window.api.stories
      .wikiHome(storyId)
      .then((result) => {
        if (!cancelled) setHomeMarkdown(result.markdown)
      })
      .catch((err) => {
        console.error('Failed to load Wiki Home:', err)
        if (!cancelled) setHomeMarkdown('_Wiki Home could not be loaded._')
      })
    return () => {
      cancelled = true
    }
  }, [selectedId, storyId, wiki])

  const commitSave = (id: string, patch: Partial<WikiPage>): void => {
    onSaveWiki(wiki.map((page) => (page.id === id ? { ...page, ...patch } : page)))
  }

  const handleCreate = (parentId: string | null = null): void => {
    const newId = createId()
    const newPage: WikiPage = {
      id: newId,
      parentId,
      name: parentId ? 'Untitled Sub-page' : 'Untitled Page',
      description: '',
      type: parentId ? wiki.find((page) => page.id === parentId)?.type || 'note' : 'character',
      body: '',
      isPrimary: false
    }
    lastSelectedIdRef.current = newId
    selectItem(newId)
    setEditName(newPage.name)
    setEditType(newPage.type)
    setEditDescription('')
    setEditBody('')
    setIsEditingBody(true)
    setExpandedCategories((prev) => ({ ...prev, [newPage.type]: true }))
    onSaveWiki([...wiki, newPage])
    posthog.capture('wiki_page_created', { page_type: newPage.type, is_sub_page: !!parentId })
  }

  const handleDelete = (id: string): void => {
    if (id === WIKI_HOME_ID) return
    setConfirmDeleteId(id)
  }

  const confirmDelete = (): void => {
    if (!confirmDeleteId) return
    const updated = wiki.filter(
      (page) => page.id !== confirmDeleteId && page.parentId !== confirmDeleteId
    )
    onSaveWiki(updated)
    if (selectedId === confirmDeleteId) {
      selectItem(WIKI_HOME_ID)
      setIsEditingBody(false)
    }
    setConfirmDeleteId(null)
  }

  const handleNameChange = (value: string): void => {
    setEditName(value)
    if (!selectedId || selectedId === WIKI_HOME_ID) return
    const activePage = wiki.find((page) => page.id === selectedId)
    if (!activePage) return
    const nextName = value || 'Untitled Page'
    const updated = wiki.map((page) => {
      const renamedBody = renameWikiPageReferences(
        page.body,
        activePage.name,
        nextName,
        activePage.id
      )
      if (page.id === selectedId) return { ...page, name: nextName, body: renamedBody }
      return renamedBody === page.body ? page : { ...page, body: renamedBody }
    })
    const updatedActiveBody = updated.find((page) => page.id === selectedId)?.body
    if (updatedActiveBody !== undefined && updatedActiveBody !== editBody) {
      setEditBody(updatedActiveBody)
    }
    onSaveWiki(updated)
  }

  const handleTypeChange = (value: string): void => {
    const normalized = value
      .toLowerCase()
      .replace(/[^a-z0-9 ]+/g, '')
      .slice(0, 64)
    setEditType(normalized)
    if (selectedId && selectedId !== WIKI_HOME_ID) commitSave(selectedId, { type: normalized })
  }

  const handleDescriptionChange = (value: string): void => {
    const clamped = value.slice(0, MAX_DESCRIPTION_CHARS)
    setEditDescription(clamped)
    if (selectedId && selectedId !== WIKI_HOME_ID) commitSave(selectedId, { description: clamped })
  }

  const handleBodyChange = (value: string): void => {
    setEditBody(value)
    if (selectedId && selectedId !== WIKI_HOME_ID) commitSave(selectedId, { body: value })
  }

  // Insert markdown tags around current selection in the textarea
  const insertMarkdown = (syntaxType: 'bold' | 'italic' | 'heading' | 'link') => {
    const textarea = textareaRef.current
    if (!textarea) return
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const text = textarea.value
    const selected = text.substring(start, end)

    let replacement = ''
    if (syntaxType === 'bold') replacement = `**${selected || 'bold text'}**`
    else if (syntaxType === 'italic') replacement = `*${selected || 'italic text'}*`
    else if (syntaxType === 'heading') replacement = `### ${selected || 'Heading'}`
    else if (syntaxType === 'link') replacement = `[[${selected || 'Page Title'}]]`

    const newBody = text.substring(0, start) + replacement + text.substring(end)
    handleBodyChange(newBody)

    // Reset cursor selection range
    setTimeout(() => {
      textarea.focus()
      const offset =
        syntaxType === 'bold' || syntaxType === 'link' ? 2 : syntaxType === 'heading' ? 4 : 1
      textarea.setSelectionRange(start + offset, start + offset + selected.length)
    }, 0)
  }

  // Get current active tab items filtered by search query
  const getFilteredWiki = (): WikiPage[] => {
    if (!searchQuery) return wiki
    const query = searchQuery.toLowerCase()
    return wiki.filter(
      (page) =>
        page.name.toLowerCase().includes(query) ||
        page.description.toLowerCase().includes(query) ||
        page.body.toLowerCase().includes(query)
    )
  }

  const filteredWiki = getFilteredWiki()

  const mainPages = filteredWiki.filter((page) => page.parentId === null)
  const subPagesByParent = new Map<string, WikiPage[]>()
  for (const page of filteredWiki) {
    if (!page.parentId) continue
    const bucket = subPagesByParent.get(page.parentId)
    if (bucket) bucket.push(page)
    else subPagesByParent.set(page.parentId, [page])
  }

  const presentTypes = Array.from(new Set(mainPages.map((page) => page.type))).sort(
    (left, right) => (left === 'rule' ? 1 : right === 'rule' ? -1 : left.localeCompare(right))
  )

  // One pending proposal per page (enforced server-side), so a page id is a safe lookup key.
  const proposalByPageId = useMemo(
    () => new Map(proposals.map((proposal) => [proposal.pageId, proposal])),
    [proposals]
  )

  const selectedProposal =
    selectedId && selectedId !== WIKI_HOME_ID ? proposalByPageId.get(selectedId) : undefined

  const handleAcceptProposal = async (proposal: WikiProposal): Promise<void> => {
    // Adopt the proposed text into the open editor ONLY after the server confirms the write.
    // These buffers only resync when the selected page CHANGES, so a failed accept that had
    // already written them would leave the editor showing content the database never received --
    // and the writer's next keystroke would persist that phantom text over what is really stored.
    const applied = await onResolveProposal(proposal.id, 'accept')
    if (!applied) return
    setEditDescription(proposal.proposedDescription)
    setEditBody(proposal.proposedBody)
  }

  const treeItems: TreeListItemData[] = [
    {
      id: WIKI_HOME_ID,
      label: <span className="wiki-tree-category-label">Wiki Home</span>,
      startContent: (
        <span className="text-primary/70">
          <BookOpen size={14} />
        </span>
      ),
      isSelected: selectedId === WIKI_HOME_ID,
      onClick: () => {
        selectItem(WIKI_HOME_ID)
        setIsEditingBody(false)
      }
    },
    ...presentTypes.map((type) => {
      const pagesOfType = mainPages.filter((page) => page.type === type)
      const isSelectedType = wiki.find((page) => page.id === selectedId)?.type === type
      return {
        id: type,
        label: <span className="wiki-tree-category-label">{typeHeading(type)}</span>,
        startContent: <span className="text-primary/70">{typeIcon(type)}</span>,
        endContent: (
          <span className={cn('wiki-count-badge', isSelectedType && 'active')}>
            {pagesOfType.length}
          </span>
        ),
        isExpanded: !!searchQuery || !!expandedCategories[type],
        children: pagesOfType.map((page) => ({
          id: page.id,
          label: <span className="wiki-tree-page-label">{page.name}</span>,
          endContent: proposalByPageId.has(page.id) ? (
            <span className="wiki-suggestion-dot" role="img" aria-label="Has a suggested update" />
          ) : undefined,
          isSelected: selectedId === page.id,
          onClick: () => {
            selectItem(page.id)
            setIsEditingBody(false)
          },
          children: (subPagesByParent.get(page.id) || []).map((sub) => ({
            id: sub.id,
            label: <span className="wiki-tree-page-label wiki-tree-sub-page">{sub.name}</span>,
            endContent: proposalByPageId.has(sub.id) ? (
              <span
                className="wiki-suggestion-dot"
                role="img"
                aria-label="Has a suggested update"
              />
            ) : undefined,
            isSelected: selectedId === sub.id,
            onClick: () => {
              selectItem(sub.id)
              setIsEditingBody(false)
            }
          }))
        }))
      }
    })
  ]

  // Click interceptor inside markdown body text to support jump-links
  const handleWikiBodyClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    const anchor = target.closest('a')
    if (anchor) {
      const href = anchor.getAttribute('href')
      if (href && href.startsWith('wiki://')) {
        e.preventDefault()
        const parts = href.replace('wiki://', '').split('/')
        const id = parts[1]
        const found = wiki.find((page) => page.id === id)
        if (found) {
          selectItem(found.id)
          setIsEditingBody(false)
        }
      }
    }
  }

  const resolveWikiLinks = (text: string): string => {
    if (!wiki || wiki.length === 0) return text
    return text.replace(/\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g, (_, target, label) => {
      const trimmedTarget = target.trim()
      const displayText = label ? label.trim() : trimmedTarget

      if (trimmedTarget.startsWith('wiki:')) {
        const parts = trimmedTarget.split(':')
        const id = parts[2]
        const found = wiki.find((page) => page.id === id)
        if (found) {
          return `<a href="wiki://${found.type}/${found.id}" class="wiki-link">${displayText}</a>`
        }
        return `<span class="wiki-link-broken">${displayText}</span>`
      }

      const found = wiki.find(
        (page) => page.name.trim().toLowerCase() === trimmedTarget.toLowerCase()
      )
      if (found) {
        return `<a href="wiki://${found.type}/${found.id}" class="wiki-link">${displayText}</a>`
      }
      return `<span class="wiki-link-broken">${displayText}</span>`
    })
  }

  // Defined at component scope (not inline in the active-page IIFE below) so the
  // react-hooks/refs lint rule can see this only ever runs from a deferred click,
  // never synchronously during render.
  const handleAddSubPage = (): void => {
    if (selectedId && selectedId !== WIKI_HOME_ID) handleCreate(selectedId)
  }

  return (
    <section className="wiki-container">
      {/* LEFT COLUMN: FILE EXPLORER SIDEBAR */}
      <aside className="wiki-explorer">
        <header className="wiki-explorer-header">
          <span className="wiki-explorer-heading-group">
            <strong className="wiki-explorer-title">Wiki</strong>
            <span className="wiki-explorer-subtitle">
              {wiki.length} {wiki.length === 1 ? 'page' : 'pages'}
            </span>
          </span>
          <IconButton
            variant="ghost"
            size="sm"
            icon={<Plus size={14} />}
            label="New Page"
            tooltip="Create a new guide page"
            onClick={() => handleCreate()}
          />
        </header>
        <section className="wiki-search">
          <TextInput
            label="Search Pages"
            isLabelHidden
            placeholder="Search pages"
            value={searchQuery}
            onChange={setSearchQuery}
            startIcon={<Search className="h-3.5 w-3.5 text-muted-foreground" />}
            hasClear
            size="sm"
          />
        </section>
        <nav className="wiki-tree" aria-label="Wiki pages">
          {searchQuery && filteredWiki.length === 0 ? (
            <section className="wiki-search-empty">
              <Search size={20} aria-hidden="true" />
              <strong>No matching pages</strong>
              <span>Try a different title or phrase.</span>
            </section>
          ) : (
            <TreeList items={treeItems} density="compact" />
          )}
        </nav>
      </aside>

      {/* RIGHT COLUMN: WORKSPACE DOCUMENT PANE */}
      <main className="wiki-workspace">
        {selectedId === WIKI_HOME_ID ? (
          <article className="wiki-document">
            <header className="wiki-document-header">
              <section className="wiki-document-heading">
                <span className="wiki-category-trigger">
                  <BookOpen size={14} /> Wiki Home
                </span>
                <span className="wiki-home-note">
                  Generated from your pages — edit a page to change what appears here.
                </span>
              </section>
            </header>
            <section className="wiki-reading-scroll" onClick={handleWikiBodyClick}>
              <article
                className="wiki-rendered-content"
                dangerouslySetInnerHTML={{ __html: marked.parse(homeMarkdown) as string }}
              />
            </section>
            <HStack gap={2}>
              <PillButton variant="ghost" size="sm" label="Sync wiki now" onClick={onSyncNow} />
            </HStack>
          </article>
        ) : selectedId === null ? (
          <section className="wiki-empty-state">
            <span className="wiki-empty-icon">
              <Sparkles size={22} />
            </span>
            <h2>Build your story world</h2>
            <p>
              Select a page from the explorer, or create a new page for a character, place, or piece
              of lore.
            </p>
            <PillButton
              onClick={() => handleCreate()}
              variant="primary"
              size="sm"
              label="Create guide page"
              icon={<Plus size={14} />}
            />
          </section>
        ) : (
          (() => {
            const activePage = wiki.find((page) => page.id === selectedId)
            if (!activePage) {
              return <section className="wiki-empty-state">Document not found or deleted.</section>
            }

            const resolvedBody = resolveWikiLinks(editBody || '')
            const parsedHTML = marked.parse(resolvedBody) as string

            return (
              <article className="wiki-document">
                {/* Document Pane Header */}
                <header className="wiki-document-header">
                  <section className="wiki-document-heading">
                    <span className="wiki-type-row">
                      <span className="wiki-category-trigger">{typeIcon(editType)}</span>
                      <input
                        type="text"
                        list="wiki-type-options"
                        value={editType}
                        onChange={(event) => handleTypeChange(event.target.value)}
                        placeholder="type"
                        className="wiki-type-input"
                        aria-label="Page type"
                      />
                      <datalist id="wiki-type-options">
                        {Array.from(new Set(wiki.map((page) => page.type))).map((type) => (
                          <option key={type} value={type} />
                        ))}
                      </datalist>
                      {activePage.parentId && (
                        <span className="wiki-parent-hint">
                          sub-page of{' '}
                          {wiki.find((page) => page.id === activePage.parentId)?.name || 'a page'}
                        </span>
                      )}
                    </span>
                    <input
                      type="text"
                      value={editName}
                      onChange={(event) => handleNameChange(event.target.value)}
                      placeholder="Page name"
                      className="wiki-title-input"
                      aria-label="Page name"
                    />
                  </section>

                  <section className="wiki-document-actions" aria-label="Page actions">
                    <IconButton
                      variant={isEditingBody ? 'primary' : 'ghost'}
                      size="sm"
                      icon={isEditingBody ? <Check size={14} /> : <Edit3 size={14} />}
                      label={isEditingBody ? 'View rendered markdown' : 'Edit body markdown'}
                      tooltip={isEditingBody ? 'Finish editing (Done)' : 'Edit page content'}
                      onClick={() => setIsEditingBody(!isEditingBody)}
                    />
                    <IconButton
                      variant="ghost"
                      size="sm"
                      icon={<Link2 size={14} />}
                      label="Copy link to clipboard"
                      tooltip="Copy wiki link"
                      onClick={() => {
                        navigator.clipboard.writeText(`[[${editName}]]`)
                      }}
                    />
                    {activePage.parentId === null && (
                      <IconButton
                        variant="ghost"
                        size="sm"
                        icon={<Plus size={14} />}
                        label="Add sub-page"
                        tooltip="Add a sub-page for extra detail"
                        onClick={handleAddSubPage}
                      />
                    )}
                    <IconButton
                      variant="ghost"
                      size="sm"
                      icon={<Trash2 size={14} className="text-destructive" />}
                      label="Delete Page"
                      tooltip="Delete Page"
                      onClick={() => handleDelete(activePage.id)}
                    />
                  </section>
                </header>

                {selectedProposal && (
                  <section className="wiki-suggestion" aria-label="Suggested update">
                    <header className="wiki-suggestion-head">
                      <strong>Suggested update</strong>
                      {selectedProposal.sourceChapterTitle && (
                        <span className="wiki-suggestion-source">
                          from {selectedProposal.sourceChapterTitle}
                        </span>
                      )}
                    </header>
                    {selectedProposal.rationale && (
                      <p className="wiki-suggestion-why">{selectedProposal.rationale}</p>
                    )}
                    <p className="wiki-suggestion-body">{selectedProposal.proposedBody}</p>
                    <HStack gap={2}>
                      <PillButton
                        variant="primary"
                        size="sm"
                        label="Accept"
                        onClick={() => handleAcceptProposal(selectedProposal)}
                      />
                      <PillButton
                        variant="ghost"
                        size="sm"
                        label="Dismiss"
                        onClick={() => onResolveProposal(selectedProposal.id, 'reject')}
                      />
                    </HStack>
                  </section>
                )}

                <section className="wiki-frontmatter">
                  <textarea
                    value={editDescription}
                    onChange={(event) => handleDescriptionChange(event.target.value)}
                    placeholder="A compact description of this page — this is what the agent sees first."
                    className="wiki-description-input"
                    rows={2}
                    aria-label="Page description"
                  />
                  <span className="wiki-frontmatter-meta">
                    <span
                      className={cn(
                        'wiki-budget',
                        editDescription.length >= MAX_DESCRIPTION_CHARS && 'over'
                      )}
                    >
                      {editDescription.length}/{MAX_DESCRIPTION_CHARS} characters
                    </span>
                    <span
                      className={cn(
                        'wiki-budget',
                        estimateTokens(editBody) > MAX_PAGE_TOKENS && 'over'
                      )}
                    >
                      ~{estimateTokens(editBody)}/{MAX_PAGE_TOKENS} tokens
                    </span>
                  </span>
                </section>

                {/* Document Pane Body */}
                {isEditingBody ? (
                  // EDIT MODE TEXTAREA
                  <section className="wiki-editor-shell">
                    {/* Markdown Editor Toolbar */}
                    <section className="wiki-toolbar">
                      <IconButton
                        variant="ghost"
                        size="sm"
                        icon={<Bold size={11} />}
                        label="Bold"
                        tooltip="Bold text"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => insertMarkdown('bold')}
                      />
                      <IconButton
                        variant="ghost"
                        size="sm"
                        icon={<Italic size={11} />}
                        label="Italic"
                        tooltip="Italic text"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => insertMarkdown('italic')}
                      />
                      <IconButton
                        variant="ghost"
                        size="sm"
                        icon={<Heading size={11} />}
                        label="Heading"
                        tooltip="Heading level 3"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => insertMarkdown('heading')}
                      />
                      <IconButton
                        variant="ghost"
                        size="sm"
                        icon={<Link2 size={11} />}
                        label="Wiki Link"
                        tooltip="Wiki Page Link"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => insertMarkdown('link')}
                      />
                      <span className="wiki-toolbar-divider" />
                      <PillButton
                        variant="ghost"
                        size="sm"
                        label="Done"
                        icon={<Check size={11} />}
                        className="h-5 text-[10px] px-1.5 animate-none"
                        onClick={() => setIsEditingBody(false)}
                      />
                    </section>

                    {/* Borderless Textarea editor */}
                    <section className="wiki-editor-content">
                      <textarea
                        ref={textareaRef}
                        value={editBody}
                        onChange={(e) => handleBodyChange(e.target.value)}
                        placeholder="Write a concise guide entry here. Author Rules should be explicit instructions the story must follow."
                        className="wiki-editor-textarea focus:ring-0"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            e.preventDefault()
                            setIsEditingBody(false)
                          }
                        }}
                      />
                    </section>
                  </section>
                ) : (
                  // READING MODE
                  <section className="wiki-reading-scroll" onClick={handleWikiBodyClick}>
                    {editBody ? (
                      <article
                        className="wiki-rendered-content"
                        dangerouslySetInnerHTML={{ __html: parsedHTML }}
                      />
                    ) : (
                      <section className="wiki-document-empty">
                        <FileText size={22} aria-hidden="true" />
                        <strong>This page is empty</strong>
                        <span>Choose edit to start adding details.</span>
                        <PillButton
                          variant="secondary"
                          size="sm"
                          label="Edit page"
                          icon={<Edit3 size={14} />}
                          onClick={() => setIsEditingBody(true)}
                        />
                      </section>
                    )}
                  </section>
                )}
              </article>
            )
          })()
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog
        isOpen={confirmDeleteId !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmDeleteId(null)
        }}
        width={400}
        purpose="required"
        className="wiki-delete-dialog"
      >
        <Layout
          header={
            <DialogHeader title="Delete Entry" onOpenChange={() => setConfirmDeleteId(null)} />
          }
          content={
            <LayoutContent>
              <p className="wiki-delete-copy">
                Are you sure you want to delete this entry? This action cannot be undone.
              </p>
            </LayoutContent>
          }
          footer={
            <LayoutFooter>
              <HStack gap={2} hAlign="end">
                <PillButton
                  variant="secondary"
                  onClick={() => setConfirmDeleteId(null)}
                  label="Cancel"
                />
                <PillButton variant="destructive" onClick={confirmDelete} label="Delete" />
              </HStack>
            </LayoutFooter>
          }
        />
      </Dialog>
    </section>
  )
}
